import { useState, useRef, useEffect, useCallback } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme, type ThemeMode } from '../hooks/useTheme';

const options: { mode: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { mode: 'light', label: 'Light', Icon: Sun },
  { mode: 'dark', label: 'Dark', Icon: Moon },
  { mode: 'system', label: 'System', Icon: Monitor },
];

export default function ThemeToggle() {
  const { mode, theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close on click-outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleSelect = useCallback(
    (selectedMode: ThemeMode) => {
      setTheme(selectedMode);
      setOpen(false);
      buttonRef.current?.focus();
    },
    [setTheme],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, selectedMode: ThemeMode) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSelect(selectedMode);
      }
    },
    [handleSelect],
  );

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        onClick={() => setOpen((prev) => !prev)}
        className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        aria-label="Toggle theme"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <div className="relative h-5 w-5">
          <Sun
            className={`absolute inset-0 h-5 w-5 transition-all duration-300 ${
              theme === 'dark'
                ? 'rotate-90 scale-0 opacity-0'
                : 'rotate-0 scale-100 opacity-100'
            }`}
            aria-hidden="true"
          />
          <Moon
            className={`absolute inset-0 h-5 w-5 transition-all duration-300 ${
              theme === 'dark'
                ? 'rotate-0 scale-100 opacity-100'
                : '-rotate-90 scale-0 opacity-0'
            }`}
            aria-hidden="true"
          />
        </div>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-36 rounded-lg border bg-popover p-1 shadow-lg"
          role="menu"
          aria-label="Theme options"
        >
          {options.map(({ mode: optMode, label, Icon }) => (
            <button
              key={optMode}
              role="menuitem"
              onClick={() => handleSelect(optMode)}
              onKeyDown={(e) => handleKeyDown(e, optMode)}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                mode === optMode
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-popover-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
